import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { LRUFactory } from 'classes';
import { PATHS, FILTER_EVENT_TYPE_OPTIONS } from 'constants';
import { getUTCDateTime } from 'utils';

import { setActiveFlight } from 'actions';

import { Row, Col, Button } from 'react-bootstrap';
import SkyPulseTable from 'components/SkyPulseTable';
import Overlay from 'containers/Overlay';

import CrewPanels from 'view/components/overlays/LRUDetail/CrewPanels';
import PassengerDisplays from 'view/components/overlays/LRUDetail/PassengerDisplays';
import PowerSupplies from 'view/components/overlays/LRUDetail/PowerSupplies';
import PublicDisplays from 'view/components/overlays/LRUDetail/PublicDisplays';
import PCUs from 'view/components/overlays/LRUDetail/PCUs';
import Servers from 'view/components/overlays/LRUDetail/Servers';
import ContentLoaders from 'view/components/overlays/LRUDetail/ContentLoaders';
import WAPs from 'view/components/overlays/LRUDetail/WAPs';

import styles from './styles.module.css';
import Events from '../FilterableEvents';

const columnDefinitions = [
  {
    name: 'UTC',
    key: 'utc',
    sortable: true,
    size: 'small',
    dataCyClassName: 'LruDetailsOverlay-EventTableColumn-Utc',
  },
  {
    name: 'Flight',
    key: 'flight',
    sortable: true,
    size: 'xsmall',
    dataCyClassName: 'LruDetailsOverlay-EventTableColumn-Flight',
  },
  {
    name: 'Event',
    key: 'event',
    sortable: true,
    size: 'xlarge',
    dataCyClassName: 'LruDetailsOverlay-EventTableColumn-Event',
  },
];

const allFilters = {};
FILTER_EVENT_TYPE_OPTIONS.forEach((item) => {
  allFilters[item.value] = true;
});

const allMinusUsageFilters = {};
FILTER_EVENT_TYPE_OPTIONS.forEach((item) => {
  if (item.value !== '5') {
    allMinusUsageFilters[item.value] = true;
  }
});

export const EVENT_FILTERS = {
  ALL: { id: 'allEvents', name: 'All events', hasOptions: false },
  ALL_MINUS_USAGE: {
    id: 'allEventsMinusUsage',
    name: 'All events minus usage',
    hasOptions: false,
  },
  SPECIFIC: { id: 'specificEvents', name: 'Specific events', hasOptions: true },
};

const idPrefix = 'LRUDetail';

const LruDetail = (props) => {
  const { id, isOpen, onClose } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const rFlightMap = useSelector(
    (state) => state.flightMap,
    () => true,
  );
  const rAircraftMap = useSelector(
    (state) => state.aircraftMap,
    () => true,
  );

  const [lruData, setData] = useState(null);
  const [dataUnavail, setDataUnavail] = useState(false);

  const flightMap = useMemo(() => {
    const map = new Map();
    rFlightMap.forEach((flight) => map.set(flight.id, flight));
    return map;
  }, [rFlightMap]);

  useEffect(() => {
    const getLRUData = async (id) => {
      try {
        const data = await LRUFactory.getLruById(id);
        setData(data);
      } catch (e) {
        console.warn('Failed to fetch LRU data:', e);
        setData(null);
        setDataUnavail(true);
      }
    };

    if (id) {
      getLRUData(id);
    } else {
      setData(null);
    }
  }, [id]);

  const aircraft = useMemo(
    () => rAircraftMap.get(lruData?.aircraft_id),
    [rAircraftMap, lruData?.aircraft_id],
  );
  const lru_type = lruData?.lru_type.value;
  const ltid = lruData?.lru_type.id;
  const position = lruData?.position;
  const deviceType = lruData
    ? aircraft.lopa.getDeviceTypeByLRUTypePosition(ltid, position)
    : null;
  const disabled = lruData === null;

  const onRowClick = (eventId, buttonId) => {
    const event = lruData.events.find((event) => event.id === eventId);

    if (buttonId === 'flightButton') {
      const flight = rFlightMap.get(event.flight_id);

      if (flight) {
        dispatch(setActiveFlight(flight));
        navigate(PATHS.flights);
      }
    }
  };

  const onCloseOverlay = () => {
    onClose();
  };

  const renderOverview = () => {
    switch (deviceType) {
      case 'waps':
        return <WAPs lruData={lruData} aircraft={aircraft} />;
      case 'power_supplies':
        return <PowerSupplies lruData={lruData} aircraft={aircraft} />;
      case 'passenger_displays':
        return <PassengerDisplays lruData={lruData} aircraft={aircraft} />;
      case 'public_displays':
        return <PublicDisplays lruData={lruData} aircraft={aircraft} />;
      case 'pcus':
        return <PCUs lruData={lruData} aircraft={aircraft} />;
      case 'crew_panels':
        return <CrewPanels lruData={lruData} aircraft={aircraft} />;
      case 'content_loaders':
        return <ContentLoaders lruData={lruData} aircraft={aircraft} />;
      case 'servers':
        return <Servers lruData={lruData} aircraft={aircraft} />;
      default:
        return (
          <Row
            data-cy-id="LruDetailsOverlay-overviewContainer"
            className={styles.overviewContainer}
            noGutters
          >
            <Col>
              <Row>
                <Col
                  data-cy-id="LruDetailsOverlay-overviewTableTitle"
                  className={styles.title}
                >
                  Overview
                </Col>
              </Row>
              <Row className={styles.overviewTable} noGutters>
                <Col>
                  <SkyPulseTable
                    tableClassname={styles.table}
                    rows={[]}
                    columns={[{ name: '--', key: '--', size: 'xlarge' }]}
                    isLoading={true && !dataUnavail}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        );
    }
  };

  const rowData = useMemo(() => {
    if (!lruData?.events) return [];

    return lruData.events.map((event) => {
      const {
        value: { default_message: defaultMessage },
      } = event.event_type;
      const displayMessage = event.message || defaultMessage;
      const flight = flightMap.get(event.flight_id);

      // if fn isn't present in flightMap, use fn from lruData event
      const flightNumber = flight?.number
        ? flight.number === '--'
          ? 'NA'
          : flight.number
        : event.flight_number;

      const flightButton = (
        <div
          className={
            flight
              ? 'skypulsetable_toggle_button'
              : styles.eventFlightNumberDisplay
          }
          data-clickable-id={'flightButton'}
          data-cy-id="LruDetailsOverlay-FlightLinkButton"
        >
          {flightNumber}
        </div>
      );

      return {
        id: event.id,
        utc: { display: getUTCDateTime(event.created), sort: event.created },
        flight: { display: flightButton, sort: flightNumber },
        event: displayMessage,
        eventTypeId: event.event_type.id,
      };
    });
  }, [lruData?.events, flightMap]);

  const eventFilterOptions = useMemo(() => {
    const options = [EVENT_FILTERS.ALL];

    if (
      lru_type === 'ITU (10.1")' ||
      lru_type === 'ITU (13.3")' ||
      lru_type === 'ITU (18.5")'
    ) {
      options.push(EVENT_FILTERS.ALL_MINUS_USAGE);
    }

    options.push(EVENT_FILTERS.SPECIFIC);

    return options;
  }, [lru_type]);

  const filterTypeConfig = {
    [EVENT_FILTERS.ALL.id]: allFilters,
    [EVENT_FILTERS.ALL_MINUS_USAGE.id]: allMinusUsageFilters,
    [EVENT_FILTERS.SPECIFIC.id]: allFilters,
  };

  const Title = () => {
    return (
      <Row className={styles.header} noGutters>
        <Col data-cy-id="LruDetailOverlay-Header">
          {!aircraft
            ? null
            : `Tail ${aircraft?.tail} | ${aircraft?.aircraft_type.value.name} | ${lruData?.lru_type.value} ${lruData?.position}`}
        </Col>
        <Col className={styles.buttonContainer}>
          <Button
            className="button_reg"
            disabled={disabled}
            data-cy-id="LruDetailOverlay-LRUDetailsReportButton"
            onClick={() => navigate(`${PATHS.reportLruDetail}/${id}`)}
          >
            LRU details reports
          </Button>
          &nbsp;
          <Button
            data-cy-id="LRU-CloseButton"
            className="button_foc"
            onClick={onCloseOverlay}
            disabled={disabled && !dataUnavail}
          >
            Close
          </Button>
        </Col>
      </Row>
    );
  };

  const Body = () => {
    return (
      <>
        <Row className={styles.body} noGutters>
          <Col className="h-100">
            {renderOverview()}
            <Events
              rowData={rowData}
              eventFilterOptions={eventFilterOptions}
              defaultFilter={EVENT_FILTERS.ALL}
              onRowClick={onRowClick}
              columnDefinitions={columnDefinitions}
              filterTypeConfig={filterTypeConfig}
              loading={!lruData}
              idPrefix={idPrefix}
            />
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Overlay
      data-cy-id="LruDetailOverlay"
      contentClassName={styles.overlayRoot}
      headerClassName="w-100"
      bodyClassName="h-100"
      title={<Title />}
      isOpen={isOpen}
      showClose={false}
      onCancel={onCloseOverlay}
    >
      {<Body />}
    </Overlay>
  );
};

export default React.memo(LruDetail);
